import { fetchCurrencies } from 'queries/currency/currency';
import React from 'react';
import {
  createContext,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { Dayjs } from 'dayjs';
import getDateRange from 'utils/dateRange/getDateRage';
import { fetchSales } from 'queries/sales/sales';

interface CurrencyContextType {
  currencies:
    | { key: number; label: string; value: number; exchange_rate: number }[]
    | undefined;
  loadCurrency: boolean;
  defaultCurrency: number | undefined;
  selectedCurrency: number | undefined;
  onSelectCurrency: (value: SetStateAction<number>) => void;
  onRangeChange: any;
  updatedSales: any;
  currency: string;
  exchangeRate: number | undefined;
  allCurrencies: any;
  enableCurrencyExchange: boolean;
  secondaryCurrency: any;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(
  undefined
);

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const refreshToken = localStorage.getItem('refreshToken');
  const { currencies, isLoading, enableCurrencyExchange } = fetchCurrencies({
    enabled: !!refreshToken,
  });

  const [selectedCurrency, setSelectedCurrency] = useState<number | undefined>(
    undefined
  );

  const formatCurrencyList = currencies?.map(
    (
      currency: {
        currency_code: string;
        shop_currency_id: number;
        is_current: boolean;
        exchange_rate: number;
      },
      index: number
    ) => ({
      key: index,
      label: currency?.currency_code,
      value: currency?.shop_currency_id,
      isDefault: currency?.is_current,
      exchange_rate: currency?.exchange_rate,
    })
  );

  const { mutate: requestSalesList, sales } = fetchSales();

  const defaultCurrency = currencies?.find(
    (item: { is_current: boolean }) => item?.is_current === true
  )?.shop_currency_id;

  const onSelectCurrency = (value: any) => {
    setSelectedCurrency(value);
  };

  const currency = currencies?.find(
    (item: { shop_currency_id: number | undefined }) =>
      item.shop_currency_id === selectedCurrency
  );

  const secondaryCurrency = currencies?.find(
    (item: any) =>
      item.shop_currency_id !== selectedCurrency && item.exchange_rate > 0
  );

  useEffect(() => {
    if (!selectedCurrency && currencies?.length > 0) {
      const setCurrentCurrency =
        formatCurrencyList.find((item: any) => item?.isDefault)?.value ||
        formatCurrencyList?.[0]?.value;
      setSelectedCurrency(setCurrentCurrency || defaultCurrency);
    }
  }, [defaultCurrency, formatCurrencyList, selectedCurrency, currencies]);

  useEffect(() => {
    if (currency?.currency_code) {
      try {
        localStorage.setItem('currency_code', currency.currency_code);
        localStorage.setItem('currency', currency.currency_code);
      } catch {}
    }
  }, [currency?.currency_code]);

  const onRangeChange = (
    dates: null | (Dayjs | null)[],
    _dateStrings: string[]
  ) => {
    if (dates) {
      const fromDate = getDateRange.formatDayjsDate(dates[0]);
      const toDate = getDateRange.formatDayjsDate(dates[1]);
      requestSalesList({
        currency_id: selectedCurrency,
        started_date: fromDate,
        ended_date: toDate,
      });
    }
  };

  return (
    <CurrencyContext.Provider
      value={{
        currencies: formatCurrencyList,
        loadCurrency: isLoading,
        defaultCurrency,
        selectedCurrency,
        onSelectCurrency,
        onRangeChange,
        updatedSales: sales,
        currency: currency?.currency_code,
        exchangeRate: currency?.exchange_rate,
        allCurrencies: currencies,
        enableCurrencyExchange: enableCurrencyExchange ?? false,
        secondaryCurrency: secondaryCurrency,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = React.useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
